/**
 * useTransformApi - Platform-Aware Transform Operations
 *
 * Routes transform operations (humanize, persona, style, summarize) to:
 * - Electron + Ollama: Local IPC handlers
 * - Web/Cloud: HTTP API to Workers AI
 *
 * Follows the useArchiveApi pattern for stability:
 * - Uses refs for non-render state
 * - Callbacks read from refs at call time
 * - Memoized return object
 *
 * @module @humanizer/studio/hooks/useTransformApi
 */

import { useCallback, useMemo } from 'react';
import { usePlatformRouter, type Backend } from './usePlatformRouter';
import type { PlatformContextValue } from '../contexts/PlatformContext';
import type { TransformResult, HumanizationResult } from '../types/transform';

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

/** Normalized result for all transform operations */
export interface NormalizedTransformResult {
  text: string;
  durationMs?: number;
  inputWordCount?: number;
  outputWordCount?: number;
  model?: string;
  backend: 'local' | 'cloud';
  /** Detection-informed intensity suggestion (Electron only) */
  suggestedIntensity?: 'subtle' | 'moderate' | 'strong' | null;
}

/** Normalized humanization result */
export interface NormalizedHumanizationResult extends NormalizedTransformResult {
  improvement?: {
    aiConfidenceDrop?: number;
    burstinessIncrease?: number;
  };
}

/** Signal snapshot for detection comparison */
export interface DetectionSignalSnapshot {
  aiLikelihood: number;
  burstiness: number;
  tellPhraseScore: number;
  ngramDiversity: number;
  semicolonRate: number;
  sentenceComplexity: number;
  // v3.1 expanded signals (optional for backward compat)
  hapaxLegomenaRate?: number;
  ttrWindowed?: number;
  compressionRatio?: number | null;
  sentenceLengthKurtosis?: number;
  contractionDensity?: number;
  punctuationContrast?: number;
  embeddingTrajectory?: number | null;
}

/** Detection comparison for closed-loop humanization */
export interface DetectionComparison {
  before: DetectionSignalSnapshot;
  after: DetectionSignalSnapshot;
  improved: boolean;
  likelihoodDrop: number;
}

/** Humanization result with detection comparison */
export interface NormalizedHumanizeWithDetectionResult extends NormalizedTransformResult {
  detection: DetectionComparison;
}

export type HumanizeGenre = 'general' | 'academic' | 'casual' | 'technical' | 'creative' | 'journalistic';

/** Persona definition */
export interface PersonaInfo {
  id: string;
  name: string;
  description?: string;
  systemPrompt?: string;
}

/** Style definition */
export interface StyleInfo {
  id: string;
  name: string;
  description?: string;
  stylePrompt?: string;
}

export interface TransformApi {
  /** Whether any transform backend is available */
  isAvailable: boolean;
  /** Which backend will be used */
  backend: Backend;
  /** Reason if unavailable */
  unavailableReason: string | null;

  /** Humanize text to sound more natural (optionally SIC-augmented) */
  humanize: (text: string, intensity?: 'light' | 'moderate' | 'aggressive', genre?: HumanizeGenre, sicFeatures?: Record<string, { score: number }>) => Promise<NormalizedHumanizationResult>;
  /** Humanize with closed-loop detection comparison (optionally SIC-augmented) */
  humanizeWithDetection: (text: string, intensity?: 'light' | 'moderate' | 'aggressive', genre?: HumanizeGenre, sicFeatures?: Record<string, { score: number }>) => Promise<NormalizedHumanizeWithDetectionResult>;
  /** Apply persona voice to text */
  persona: (text: string, personaId: string) => Promise<NormalizedTransformResult>;
  /** Apply custom persona */
  personaCustom: (text: string, name: string, systemPrompt: string) => Promise<NormalizedTransformResult>;
  /** Apply style transformation */
  style: (text: string, styleId: string) => Promise<NormalizedTransformResult>;
  /** Apply custom style */
  styleCustom: (text: string, name: string, stylePrompt: string) => Promise<NormalizedTransformResult>;
  /** Summarize text */
  summarize: (text: string, style?: 'brief' | 'detailed') => Promise<NormalizedTransformResult>;
  /** Generate embeddings */
  embed: (text: string) => Promise<{ embedding: number[] }>;
  /** List available personas */
  listPersonas: () => Promise<{ personas: PersonaInfo[] }>;
  /** List available styles */
  listStyles: () => Promise<{ styles: StyleInfo[] }>;
  /** Synthesize voice from samples (cloud-only for now) */
  synthesizeVoice: (samples: string[]) => Promise<{ persona: PersonaInfo }>;
}

// ═══════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// FALLBACK PRESETS (mirrors @humanizer/npe BUILTIN_PERSONAS & BUILTIN_STYLES)
// ═══════════════════════════════════════════════════════════════════════════

export const FALLBACK_PERSONAS: PersonaInfo[] = [
  { id: 'empiricist', name: 'Empiricist', description: 'An empiricist narrator who trusts observation over theory.' },
  { id: 'romantic', name: 'Romantic', description: 'A romantic narrator who sees meaning in emotion and connection.' },
  { id: 'stoic', name: 'Stoic', description: 'A stoic narrator who distinguishes what can and cannot be controlled.' },
  { id: 'absurdist', name: 'Absurdist', description: 'An absurdist narrator who sees cosmic comedy in human striving.' },
  { id: 'darwin_systematic', name: 'Darwin (Systematic Naturalist)', description: 'A systematic naturalist who classifies and connects.' },
  { id: 'holmes_analytical', name: 'Holmes (Analytical)', description: 'An analytical mind that deduces from details others miss.' },
  { id: 'watson_documenter', name: 'Watson (Careful Documenter)', description: 'A careful documenter and observer.' },
  { id: 'austen_ironic', name: 'Austen (Ironic Observer)', description: 'An ironic social observer in the style of Jane Austen.' },
  { id: 'dickens_humanitarian', name: 'Dickens (Humanitarian)', description: 'A humanitarian narrator in the tradition of Charles Dickens.' },
  { id: 'thoreau_contemplative', name: 'Thoreau (Contemplative)', description: 'A contemplative naturalist observing nature and simplicity.' },
  { id: 'emerson_transcendent', name: 'Emerson (Transcendent)', description: 'A transcendentalist voice awakening latent capacities.' },
  { id: 'montaigne_reflective', name: 'Montaigne (Reflective Essayist)', description: 'A reflective essayist exploring what it means to be human.' },
  { id: 'marcus_aurelius_meditative', name: 'Marcus Aurelius (Meditative)', description: 'A meditative philosopher-ruler reflecting on duty and reason.' },
  { id: 'tech_optimist', name: 'Tech Optimist', description: 'A technology optimist who sees tools enabling human flourishing.' },
  { id: 'skeptical_analyst', name: 'Skeptical Analyst', description: 'A skeptical analyst who questions assumptions and examines incentives.' },
  { id: 'curious_generalist', name: 'Curious Generalist', description: 'A curious generalist who finds connections across domains.' },
];

export const FALLBACK_STYLES: StyleInfo[] = [
  { id: 'academic', name: 'Academic', description: 'Formal academic register with precise vocabulary.' },
  { id: 'technical', name: 'Technical', description: 'Precise technical language.' },
  { id: 'legal', name: 'Legal', description: 'Legal prose with careful qualification.' },
  { id: 'journalistic', name: 'Journalistic', description: 'Clear, concise journalistic style.' },
  { id: 'executive_summary', name: 'Executive Summary', description: 'Lead with conclusions, then evidence.' },
  { id: 'conversational', name: 'Conversational', description: 'Warm, accessible, conversational style.' },
  { id: 'reddit_casual', name: 'Reddit Casual', description: 'Casual internet prose, informal but articulate.' },
  { id: 'blog_personal', name: 'Personal Blog', description: 'Personal blog style with authentic voice.' },
  { id: 'literary', name: 'Literary', description: 'Rich literary prose with figurative language.' },
  { id: 'austen_precision', name: 'Austen Precision', description: 'Precision prose in the style of Jane Austen.' },
  { id: 'dickens_dramatic', name: 'Dickens Dramatic', description: 'Dramatic prose in the style of Charles Dickens.' },
  { id: 'hemingway_sparse', name: 'Hemingway Sparse', description: 'Sparse, direct prose in the Hemingway style.' },
  { id: 'orwell_clear', name: 'Orwell Clear', description: 'Transparent clarity in the Orwell tradition.' },
  { id: 'scientific', name: 'Scientific', description: 'Third person, hedged conclusions, zero emotional coloring.' },
  { id: 'philosophical', name: 'Philosophical', description: 'Careful definition, explicit argument structure.' },
];

export function useTransformApi(): TransformApi {
  const { getBackend, isElectron, isAvailable, backend, unavailableReason, cloudApi } = usePlatformRouter({
    resolveCapabilities(platform: PlatformContextValue | null, isElectron: boolean) {
      if (platform) {
        return {
          canUseLocal: platform.canUseLocalLLM,
          canUseCloud: platform.canUseCloudLLM,
          preferredBackend: platform.preferredLLMBackend,
        };
      }
      return {
        canUseLocal: false,
        canUseCloud: !isElectron,
        preferredBackend: isElectron ? 'none' as const : 'cloud' as const,
      };
    },
    resolveAvailability(platform: PlatformContextValue | null, isElectron: boolean) {
      const isAvailable = platform
        ? platform.canUseLocalLLM || platform.canUseCloudLLM
        : !isElectron;
      const backend = platform?.preferredLLMBackend ?? (isElectron ? 'none' as const : 'cloud' as const);
      const unavailableReason = isAvailable
        ? null
        : isElectron
          ? 'Ollama not available. Start Ollama to enable transformations.'
          : 'Sign in to use cloud transformations.';
      return { isAvailable, backend, unavailableReason };
    },
    platformDeps: (platform) => [platform?.canUseLocalLLM, platform?.canUseCloudLLM, platform?.preferredLLMBackend],
  });

  const getUnavailableReason = useCallback((): string | null => {
    if (isElectron) {
      return 'Ollama not available. Start Ollama to enable transformations.';
    }
    return 'Sign in to use cloud transformations.';
  }, [isElectron]);

  // ─────────────────────────────────────────────────────────────────────────
  // TRANSFORM OPERATIONS
  // ─────────────────────────────────────────────────────────────────────────

  const humanize = useCallback(async (
    text: string,
    intensity: 'light' | 'moderate' | 'aggressive' = 'moderate',
    genre?: HumanizeGenre,
    sicFeatures?: Record<string, { score: number }>,
  ): Promise<NormalizedHumanizationResult> => {
    const currentBackend = getBackend();

    if (currentBackend === 'local') {
      const electronTransform = window.electronTransform;
      if (!electronTransform) {
        throw new Error('Local transform API not available');
      }
      // Map intensity from hook params (light/moderate/aggressive) to Electron IPC (subtle/moderate/strong)
      const localIntensity = intensity === 'light' ? 'subtle' : intensity === 'aggressive' ? 'strong' : 'moderate';
      const result = await electronTransform.humanize(text, localIntensity, genre, sicFeatures);
      return {
        text: result.text,
        durationMs: result.durationMs,
        inputWordCount: result.inputWordCount,
        outputWordCount: result.outputWordCount,
        model: result.model,
        backend: 'local',
        suggestedIntensity: result.suggestedIntensity ?? null,
      };
    }

    if (currentBackend === 'cloud') {
      const result: HumanizationResult = await cloudApi.transform.humanize(text, intensity);
      return {
        text: result.humanizedText,
        durationMs: result.processing?.totalDurationMs,
        backend: 'cloud',
        improvement: result.improvement,
      };
    }

    throw new Error(getUnavailableReason() || 'No transform backend available');
  }, [getBackend, getUnavailableReason, cloudApi]);

  const humanizeWithDetection = useCallback(async (
    text: string,
    intensity: 'light' | 'moderate' | 'aggressive' = 'moderate',
    genre?: HumanizeGenre,
    sicFeatures?: Record<string, { score: number }>,
  ): Promise<NormalizedHumanizeWithDetectionResult> => {
    const currentBackend = getBackend();

    if (currentBackend === 'local') {
      const electronTransform = window.electronTransform;
      if (!electronTransform?.humanizeWithDetection) {
        throw new Error('Closed-loop humanization not available');
      }
      const localIntensity = intensity === 'light' ? 'subtle' : intensity === 'aggressive' ? 'strong' : 'moderate';
      const result = await electronTransform.humanizeWithDetection(text, localIntensity, genre, sicFeatures);
      return {
        text: result.text,
        durationMs: result.durationMs,
        inputWordCount: result.inputWordCount,
        outputWordCount: result.outputWordCount,
        model: result.model,
        backend: 'local',
        detection: result.detection,
      };
    }

    // Cloud: run detect → humanize → re-detect for closed-loop comparison
    if (currentBackend === 'cloud') {
      // Run detection on original text
      const beforeDetection = await cloudApi.analyze.detect(text);
      const beforeSnapshot: DetectionSignalSnapshot = {
        aiLikelihood: beforeDetection.aiLikelihood ?? 0,
        burstiness: beforeDetection.features?.burstiness ?? 0,
        tellPhraseScore: beforeDetection.features?.tellPhraseScore ?? 0,
        ngramDiversity: beforeDetection.features?.ngramDiversity ?? 0,
        semicolonRate: beforeDetection.features?.semicolonRate ?? 0,
        sentenceComplexity: beforeDetection.features?.sentenceComplexity ?? 0,
      };

      // Humanize
      const result: HumanizationResult = await cloudApi.transform.humanize(text, intensity);
      const humanizedText = result.humanizedText;

      // Run detection on humanized text
      const afterDetection = await cloudApi.analyze.detect(humanizedText);
      const afterSnapshot: DetectionSignalSnapshot = {
        aiLikelihood: afterDetection.aiLikelihood ?? 0,
        burstiness: afterDetection.features?.burstiness ?? 0,
        tellPhraseScore: afterDetection.features?.tellPhraseScore ?? 0,
        ngramDiversity: afterDetection.features?.ngramDiversity ?? 0,
        semicolonRate: afterDetection.features?.semicolonRate ?? 0,
        sentenceComplexity: afterDetection.features?.sentenceComplexity ?? 0,
      };

      return {
        text: humanizedText,
        durationMs: result.processing?.totalDurationMs,
        backend: 'cloud',
        detection: {
          before: beforeSnapshot,
          after: afterSnapshot,
          improved: afterSnapshot.aiLikelihood < beforeSnapshot.aiLikelihood,
          likelihoodDrop: beforeSnapshot.aiLikelihood - afterSnapshot.aiLikelihood,
        },
      };
    }

    throw new Error(getUnavailableReason() || 'No transform backend available');
  }, [getBackend, getUnavailableReason, cloudApi]);

  const persona = useCallback(async (
    text: string,
    personaId: string
  ): Promise<NormalizedTransformResult> => {
    const currentBackend = getBackend();

    if (currentBackend === 'local') {
      const electronTransform = window.electronTransform;
      if (!electronTransform) {
        throw new Error('Local transform API not available');
      }
      const result = await electronTransform.persona(text, personaId);
      return { ...result, backend: 'local' };
    }

    if (currentBackend === 'cloud') {
      const result: TransformResult = await cloudApi.transform.persona(text, personaId);
      return { ...result, backend: 'cloud' };
    }

    throw new Error(getUnavailableReason() || 'No transform backend available');
  }, [getBackend, getUnavailableReason, cloudApi]);

  const personaCustom = useCallback(async (
    text: string,
    name: string,
    systemPrompt: string
  ): Promise<NormalizedTransformResult> => {
    const currentBackend = getBackend();

    if (currentBackend === 'local') {
      const electronTransform = window.electronTransform;
      if (!electronTransform) {
        throw new Error('Local transform API not available');
      }
      // Local uses description string
      const result = await electronTransform.personaCustom(text, `${name}: ${systemPrompt}`);
      return { ...result, backend: 'local' };
    }

    if (currentBackend === 'cloud') {
      const result: TransformResult = await cloudApi.transform.personaCustom(text, { name, systemPrompt });
      return { ...result, backend: 'cloud' };
    }

    throw new Error(getUnavailableReason() || 'No transform backend available');
  }, [getBackend, getUnavailableReason, cloudApi]);

  const style = useCallback(async (
    text: string,
    styleId: string
  ): Promise<NormalizedTransformResult> => {
    const currentBackend = getBackend();

    if (currentBackend === 'local') {
      const electronTransform = window.electronTransform;
      if (!electronTransform) {
        throw new Error('Local transform API not available');
      }
      const result = await electronTransform.style(text, styleId);
      return { ...result, backend: 'local' };
    }

    if (currentBackend === 'cloud') {
      const result: TransformResult = await cloudApi.transform.style(text, styleId);
      return { ...result, backend: 'cloud' };
    }

    throw new Error(getUnavailableReason() || 'No transform backend available');
  }, [getBackend, getUnavailableReason, cloudApi]);

  const styleCustom = useCallback(async (
    text: string,
    name: string,
    stylePrompt: string
  ): Promise<NormalizedTransformResult> => {
    const currentBackend = getBackend();

    if (currentBackend === 'local') {
      const electronTransform = window.electronTransform;
      if (!electronTransform) {
        throw new Error('Local transform API not available');
      }
      // Local uses instructions string
      const result = await electronTransform.styleCustom(text, stylePrompt);
      return { ...result, backend: 'local' };
    }

    if (currentBackend === 'cloud') {
      const result: TransformResult = await cloudApi.transform.styleCustom(text, { name, stylePrompt });
      return { ...result, backend: 'cloud' };
    }

    throw new Error(getUnavailableReason() || 'No transform backend available');
  }, [getBackend, getUnavailableReason, cloudApi]);

  const summarize = useCallback(async (
    text: string,
    style: 'brief' | 'detailed' = 'brief'
  ): Promise<NormalizedTransformResult> => {
    const currentBackend = getBackend();

    if (currentBackend === 'local') {
      const electronTransform = window.electronTransform;
      if (!electronTransform) {
        throw new Error('Local transform API not available');
      }
      // Map style to local targetLength
      const targetLength = style === 'brief' ? 'brief' : 'detailed';
      const result = await electronTransform.summarize(text, targetLength);
      return { ...result, backend: 'local' };
    }

    if (currentBackend === 'cloud') {
      const result = await cloudApi.transform.summarize(text, style);
      return {
        text: result.summary,
        backend: 'cloud',
      };
    }

    throw new Error(getUnavailableReason() || 'No transform backend available');
  }, [getBackend, getUnavailableReason, cloudApi]);

  const embed = useCallback(async (text: string): Promise<{ embedding: number[] }> => {
    const currentBackend = getBackend();

    if (currentBackend === 'local') {
      const electronOllama = window.electronOllama;
      if (!electronOllama) {
        throw new Error('Local embedding API not available');
      }
      const result = await electronOllama.embed(text);
      return { embedding: result.embedding };
    }

    if (currentBackend === 'cloud') {
      // Cloud embed endpoint only chunks text — it does not generate embedding vectors.
      // Embedding generation requires a vector model (Ollama local or future Cloudflare Vectors).
      throw new Error('Cloud embedding not yet implemented. Use local Ollama for embeddings.');
    }

    throw new Error(getUnavailableReason() || 'No embedding backend available');
  }, [getBackend, getUnavailableReason, cloudApi]);

  const listPersonas = useCallback(async (): Promise<{ personas: PersonaInfo[] }> => {
    const currentBackend = getBackend();

    if (currentBackend === 'local') {
      const electronTransform = window.electronTransform;
      if (electronTransform?.listPersonas) {
        try {
          const result = await electronTransform.listPersonas();
          if (result.personas.length > 0) return result;
        } catch { /* fall through to fallback */ }
      }
      return { personas: FALLBACK_PERSONAS.map(p => ({ ...p, isSystem: true })) };
    }

    if (currentBackend === 'cloud') {
      try {
        const result = await cloudApi.transform.listPersonas();
        if (result.personas.length > 0) return result;
      } catch { /* fall through to fallback */ }
    }

    return { personas: FALLBACK_PERSONAS.map(p => ({ ...p, isSystem: true })) };
  }, [getBackend, cloudApi]);

  const listStyles = useCallback(async (): Promise<{ styles: StyleInfo[] }> => {
    const currentBackend = getBackend();

    if (currentBackend === 'local') {
      const electronTransform = window.electronTransform;
      if (electronTransform?.listStyles) {
        try {
          const result = await electronTransform.listStyles();
          if (result.styles.length > 0) return result;
        } catch { /* fall through to fallback */ }
      }
      return { styles: FALLBACK_STYLES.map(s => ({ ...s, isSystem: true })) };
    }

    if (currentBackend === 'cloud') {
      try {
        const result = await cloudApi.transform.listStyles();
        if (result.styles.length > 0) return result;
      } catch { /* fall through to fallback */ }
    }

    return { styles: FALLBACK_STYLES.map(s => ({ ...s, isSystem: true })) };
  }, [getBackend, cloudApi]);

  const synthesizeVoice = useCallback(async (samples: string[]): Promise<{ persona: PersonaInfo }> => {
    const currentBackend = getBackend();

    if (currentBackend === 'local') {
      // Voice synthesis requires LLM analysis - use Ollama
      const electronOllama = window.electronOllama;
      if (!electronOllama) {
        throw new Error('Voice synthesis requires Ollama. Please start Ollama.');
      }

      // Synthesize by analyzing samples with LLM
      const analysisPrompt = `Analyze these writing samples and create a voice profile:

${samples.map((s, i) => `Sample ${i + 1}: "${s}"`).join('\n\n')}

Extract the key characteristics: tone, vocabulary level, sentence structure, personality traits.
Return a JSON object with: name (suggested voice name), description (brief description), systemPrompt (instructions to write in this voice).`;

      const result = await electronOllama.complete('You are a voice analysis expert.', analysisPrompt);
      const parsed = JSON.parse(result);

      return {
        persona: {
          id: `custom-${Date.now()}`,
          name: parsed.name || 'Custom Voice',
          description: parsed.description,
          systemPrompt: parsed.systemPrompt,
        },
      };
    }

    if (currentBackend === 'cloud') {
      const result = await cloudApi.transform.synthesizeVoice(samples);
      return {
        persona: {
          id: `cloud-${Date.now()}`,
          name: result.persona.name,
          description: result.persona.systemPrompt?.substring(0, 100),
          systemPrompt: result.persona.systemPrompt,
        },
      };
    }

    throw new Error(getUnavailableReason() || 'Voice synthesis not available');
  }, [getBackend, getUnavailableReason, cloudApi]);

  // Memoize the returned object for stability
  return useMemo(() => ({
    isAvailable,
    backend,
    unavailableReason,
    humanize,
    humanizeWithDetection,
    persona,
    personaCustom,
    style,
    styleCustom,
    summarize,
    embed,
    listPersonas,
    listStyles,
    synthesizeVoice,
  }), [
    isAvailable,
    backend,
    unavailableReason,
    humanize,
    humanizeWithDetection,
    persona,
    personaCustom,
    style,
    styleCustom,
    summarize,
    embed,
    listPersonas,
    listStyles,
    synthesizeVoice,
  ]);
}

export default useTransformApi;
